"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vehicle = exports.vehicleAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.vehicleAttributes = [
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    'make',
    'model',
    'type',
    'capacitySeated',
    'capacityStanding',
    'modelYear',
    'isElectric',
    'isPluginHybrid',
    'isHybrid',
    'isHydrogen',
    'acquiredYear',
    'licensePlateNumber',
    'internalId',
    'preData'
];
/**
 * A vehicle is owned by a person or an organization
 * and could be used during a trip or a segment of a trip
 * It could include cars, trucks, planes, buses, boats, bicycles, scooters, etc.
 */
class Vehicle extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        for (const attribute in (0, omit_1.default)(params, ['_organizationUuid', '_ownerUuid'])) {
            if (exports.vehicleAttributes.includes(attribute)) {
                this._attributes[attribute] = params[attribute];
            }
            else {
                this._customAttributes[attribute] = params[attribute];
            }
        }
        this.organizationUuid = params._organizationUuid;
        this.ownerUuid = params._ownerUuid;
        this._surveyObjectsRegistry.registerVehicle(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get make() {
        return this._attributes.make;
    }
    set make(value) {
        this._attributes.make = value;
    }
    get model() {
        return this._attributes.model;
    }
    set model(value) {
        this._attributes.model = value;
    }
    get type() {
        return this._attributes.type;
    }
    set type(value) {
        this._attributes.type = value;
    }
    get capacitySeated() {
        return this._attributes.capacitySeated;
    }
    set capacitySeated(value) {
        this._attributes.capacitySeated = value;
    }
    get capacityStanding() {
        return this._attributes.capacityStanding;
    }
    set capacityStanding(value) {
        this._attributes.capacityStanding = value;
    }
    get modelYear() {
        return this._attributes.modelYear;
    }
    set modelYear(value) {
        this._attributes.modelYear = value;
    }
    get isElectric() {
        return this._attributes.isElectric;
    }
    set isElectric(value) {
        this._attributes.isElectric = value;
    }
    get isPluginHybrid() {
        return this._attributes.isPluginHybrid;
    }
    set isPluginHybrid(value) {
        this._attributes.isPluginHybrid = value;
    }
    get isHybrid() {
        return this._attributes.isHybrid;
    }
    set isHybrid(value) {
        this._attributes.isHybrid = value;
    }
    get isHydrogen() {
        return this._attributes.isHydrogen;
    }
    set isHydrogen(value) {
        this._attributes.isHydrogen = value;
    }
    get acquiredYear() {
        return this._attributes.acquiredYear;
    }
    set acquiredYear(value) {
        this._attributes.acquiredYear = value;
    }
    get licensePlateNumber() {
        return this._attributes.licensePlateNumber;
    }
    set licensePlateNumber(value) {
        this._attributes.licensePlateNumber = value;
    }
    get internalId() {
        return this._attributes.internalId;
    }
    set internalId(value) {
        this._attributes.internalId = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get ownerUuid() {
        return this._ownerUuid;
    }
    set ownerUuid(value) {
        this._ownerUuid = value;
    }
    get owner() {
        if (!this._ownerUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getPerson(this._ownerUuid);
    }
    get organizationUuid() {
        return this._organizationUuid;
    }
    set organizationUuid(value) {
        this._organizationUuid = value;
    }
    get organization() {
        if (!this._organizationUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getOrganization(this._organizationUuid);
    }
    /**
     * Creates a Vehicle object from sanitized parameters
     * @param {ExtendedVehicleAttributes | SerializedExtendedVehicleAttributes} params - Sanitized vehicle parameters
     * @returns {Vehicle} New Vehicle instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Vehicle(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Vehicle.validateParams(dirtyParams);
        const vehicle = errors.length === 0 ? new Vehicle(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(vehicle);
    }
    static validateParams(dirtyParams, displayName = 'Vehicle') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('make', dirtyParams.make, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('model', dirtyParams.model, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('type', dirtyParams.type, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('capacitySeated', dirtyParams.capacitySeated, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('capacityStanding', dirtyParams.capacityStanding, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('modelYear', dirtyParams.modelYear, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isElectric', dirtyParams.isElectric, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isPluginHybrid', dirtyParams.isPluginHybrid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isHybrid', dirtyParams.isHybrid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('isHydrogen', dirtyParams.isHydrogen, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('acquiredYear', dirtyParams.acquiredYear, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('licensePlateNumber', dirtyParams.licensePlateNumber, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('internalId', dirtyParams.internalId, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_ownerUuid', dirtyParams._ownerUuid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_organizationUuid', dirtyParams._organizationUuid, displayName));
        return errors;
    }
}
exports.Vehicle = Vehicle;
Vehicle._confidentialAttributes = ['licensePlateNumber', 'internalId', 'preData'];
//# sourceMappingURL=Vehicle.js.map